/**
 * 顶部导航栏：包含 Logo、主导航链接和用户操作区。
 */

import type { ReactElement } from 'react'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Bot,
  ChevronDown,
  CreditCard,
  History,
  Image,
  LayoutGrid,
  LogIn,
  LogOut,
  Megaphone,
  ReceiptText,
  Server,
  ShieldAlert,
  Sparkles,
  User,
  X,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useLatestSystemUpdate } from '@/hooks/useSystemUpdates'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores'
import type { ComputeType } from '@/types/auth'
import { MobileMenu } from './MobileMenu'

/**
 * 记录子菜单配置。
 */
const RECORDS_LINKS = [
  { label: '生图记录', href: '/records/generations', icon: History },
  { label: '工具记录', href: '/records/tools', icon: Sparkles },
  { label: '算力流水', href: '/records/billing', icon: Zap },
  { label: '充值记录', href: '/records/recharges', icon: ReceiptText },
] as const

/**
 * 记录实用工具子菜单配置。
 */
const UTILITY_LINKS = [
  { label: '敏感词过滤', href: '/tools/sensitive-word-filter', icon: ShieldAlert },
] as const

interface MembershipBanner {
  message: string
  expired: boolean
}

const DAY_MS = 24 * 60 * 60 * 1000
const DISMISSED_SYSTEM_UPDATE_KEY = 'dismissed-system-update-id' as const
const CLIENT_ASSISTANT_NAV_HASH = '#client-assistant-download' as const
const CLIENT_ASSISTANT_NAV_HREF = `/pricing${CLIENT_ASSISTANT_NAV_HASH}` as const

/**
 * 获取本地记录的已关闭公告 ID。
 * @returns 已关闭公告 ID
 */
function getDismissedSystemUpdateID(): string | null {
  try {
    return localStorage.getItem(DISMISSED_SYSTEM_UPDATE_KEY)
  } catch {
    return null
  }
}

/**
 * 写入本地已关闭公告 ID。
 * @param updateID 公告 ID
 */
function setDismissedSystemUpdateID(updateID: string): void {
  try {
    localStorage.setItem(DISMISSED_SYSTEM_UPDATE_KEY, updateID)
  } catch {
    // localStorage 不可用时静默失败
  }
}

/**
 * 格式化公告发布时间。
 * @param value 时间字符串
 * @returns 格式化后的时间
 */
function formatPublishTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function getMembershipBanner(exclusiveExpireAt: string | undefined): MembershipBanner | null {
  if (exclusiveExpireAt === undefined || exclusiveExpireAt === '') {
    return null
  }

  const expireAt = new Date(exclusiveExpireAt)
  if (Number.isNaN(expireAt.getTime())) {
    return null
  }

  const diffMs = expireAt.getTime() - Date.now()
  const formattedExpireAt = expireAt.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  if (diffMs <= 0) {
    return {
      message: `专享会员已到期（${formattedExpireAt}），请及时续费`,
      expired: true,
    }
  }

  const daysLeft = Math.ceil(diffMs / DAY_MS)
  if (daysLeft > 3) {
    return null
  }

  const remainText = diffMs < DAY_MS ? '不足 1 天' : `${String(daysLeft)} 天`
  return {
    message: `专享会员将在 ${formattedExpireAt} 到期（剩余 ${remainText}）`,
    expired: false,
  }
}

/**
 * 顶部导航栏组件。
 * @returns ReactElement
 */
export function Navbar(): ReactElement {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const [dismissedSystemUpdateID, setDismissedSystemUpdateIDState] = useState<string | null>(
    getDismissedSystemUpdateID
  )
  const { data: latestSystemUpdate } = useLatestSystemUpdate()
  const credits = user?.company?.balance ?? 0
  const computeType = user?.company?.computeType ?? 'shared'
  const membershipBanner = getMembershipBanner(user?.company?.exclusiveExpireAt)
  const isClientAssistantNavActive =
    location.pathname === '/pricing' && location.hash === CLIENT_ASSISTANT_NAV_HASH
  const shouldShowSystemUpdateBanner =
    latestSystemUpdate !== null &&
    latestSystemUpdate !== undefined &&
    latestSystemUpdate.id !== dismissedSystemUpdateID

  /**
   * 处理退出登录
   * 清除用户状态并跳转到首页
   */
  function handleLogout(): void {
    logout()
    toast.success('退出登录成功')
    void navigate('/')
  }

  /**
   * 关闭系统更新横幅
   */
  function handleDismissSystemUpdateBanner(): void {
    if (latestSystemUpdate === null || latestSystemUpdate === undefined) {
      return
    }
    setDismissedSystemUpdateID(latestSystemUpdate.id)
    setDismissedSystemUpdateIDState(latestSystemUpdate.id)
  }

  /**
   * 判断导航链接是否激活。
   * @param href 链接地址
   * @returns 是否激活
   */
  function isLinkActive(href: string): boolean {
    if (href === '/workflows') {
      return location.pathname === '/' || location.pathname.startsWith('/workflows')
    }
    if (href === '/pricing') {
      return location.pathname === '/pricing' && !isClientAssistantNavActive
    }
    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  /**
   * 判断记录下拉菜单是否激活。
   * @returns 是否激活
   */
  function isRecordsActive(): boolean {
    return location.pathname.startsWith('/records')
  }

  /**
   * 判断实用工具下拉菜单是否激活。
   * @returns 是否激活
   */
  function isToolsActive(): boolean {
    return location.pathname.startsWith('/tools')
  }

  /**
   * 获取算力类型展示文案
   * @param type 算力类型
   * @returns 展示文案
   */
  function getComputeTypeLabel(type: ComputeType): string {
    return type === 'exclusive' ? '专享算力' : '共享算力'
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md">
      {membershipBanner !== null ? (
        <div
          className={cn(
            'border-b',
            membershipBanner.expired
              ? 'border-red-500/20 bg-red-500/10'
              : 'border-amber-500/20 bg-amber-500/10'
          )}
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 sm:px-6 lg:px-8">
            <p
              className={cn(
                'flex items-center gap-1.5 text-xs sm:text-sm',
                membershipBanner.expired
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-amber-700 dark:text-amber-300'
              )}
            >
              <AlertTriangle className="h-4 w-4" />
              <span>{membershipBanner.message}</span>
            </p>
            <Button asChild size="sm" variant="outline" className="h-7 shrink-0 px-2 text-xs">
              <Link to="/pricing">去续费</Link>
            </Button>
          </div>
        </div>
      ) : null}
      {shouldShowSystemUpdateBanner ? (
        <div className="border-b border-blue-500/20 bg-blue-500/10">
          <div className="mx-auto flex max-w-7xl items-start justify-between gap-3 px-4 py-2 sm:items-center sm:px-6 lg:px-8">
            <p className="flex flex-1 items-start gap-1.5 text-xs text-blue-700 sm:items-center sm:text-sm">
              <Megaphone className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0" />
              <span className="line-clamp-2">
                系统更新：{latestSystemUpdate.title}（{formatPublishTime(latestSystemUpdate.publishedAt)}）
              </span>
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <Button asChild size="sm" variant="outline" className="h-7 px-2 text-xs">
                <Link to={`/updates/${latestSystemUpdate.id}`}>查看详情</Link>
              </Button>
              <button
                type="button"
                onClick={handleDismissSystemUpdateBanner}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-blue-700/70 transition-colors hover:bg-blue-500/10 hover:text-blue-800"
                aria-label="关闭系统更新提示"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* 左侧：Logo + 导航链接 */}
        <div className="flex items-center gap-8">
          {/* Mobile Menu */}
          <MobileMenu />

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 font-bold text-lg transition-opacity hover:opacity-90"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg shadow-primary/20">
              <Sparkles className="h-5 w-5" />
            </div>
            <span className="hidden tracking-tight sm:inline-block">月汐AI</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-1 md:flex">
            {[
              { label: '工作流', href: '/workflows', icon: LayoutGrid },
              ...(computeType === 'exclusive'
                ? [{ label: '算力管理', href: '/exclusive-compute', icon: Server }]
                : []),
              { label: '购买算力', href: '/pricing', icon: CreditCard },
              { label: '采集助手下载', href: CLIENT_ASSISTANT_NAV_HREF, icon: Bot },
            ].map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'relative px-3 py-2 text-sm font-medium transition-colors',
                  (link.href === CLIENT_ASSISTANT_NAV_HREF
                    ? isClientAssistantNavActive
                    : isLinkActive(link.href))
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {link.label}
                {(link.href === CLIENT_ASSISTANT_NAV_HREF
                  ? isClientAssistantNavActive
                  : isLinkActive(link.href)) && (
                  <span className="absolute inset-x-3 -bottom-[17px] h-0.5 rounded-full bg-primary" />
                )}
              </Link>
            ))}
            <a
              href="https://pod.yuexiai.com/"
              target="_blank"
              rel="noreferrer noopener"
              className="relative px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              美国本土POD工厂
            </a>

            {/* 记录下拉菜单 */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'relative flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors',
                    isRecordsActive()
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  记录
                  <ChevronDown className="h-4 w-4" />
                  {isRecordsActive() && (
                    <span className="absolute inset-x-3 -bottom-[17px] h-0.5 rounded-full bg-primary" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-40">
                {RECORDS_LINKS.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link
                      to={link.href}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <link.icon className="h-4 w-4" />
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 实用工具下拉菜单 */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'relative flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors',
                    isToolsActive()
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  实用工具
                  <ChevronDown className="h-4 w-4" />
                  {isToolsActive() && (
                    <span className="absolute inset-x-3 -bottom-[17px] h-0.5 rounded-full bg-primary" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44">
                {UTILITY_LINKS.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link to={link.href} className="flex cursor-pointer items-center gap-2">
                      <link.icon className="h-4 w-4" />
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>

        {/* 右侧：算力 + 用户操作 */}
        <div className="flex items-center gap-3">
          {/* 用户区域 */}
          {user === null ? (
            <Button asChild size="sm">
              <Link to="/login">
                <LogIn className="h-4 w-4" />
                登录
              </Link>
            </Button>
          ) : (
            <>
              {/* 算力类型 - 仅登录后显示 */}
              <div
                className={cn(
                  'hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-sm sm:flex',
                  computeType === 'exclusive'
                    ? 'bg-gradient-to-r from-emerald-500/10 to-cyan-500/10'
                    : 'bg-secondary'
                )}
              >
                <Zap
                  className={cn(
                    'h-4 w-4',
                    computeType === 'exclusive' ? 'text-emerald-500' : 'text-muted-foreground'
                  )}
                />
                <span
                  className={cn(
                    'font-medium',
                    computeType === 'exclusive'
                      ? 'text-emerald-600'
                      : 'text-muted-foreground'
                  )}
                >
                  {getComputeTypeLabel(computeType)}
                </span>
              </div>

              {/* 算力显示 - 仅登录后显示 */}
              <div className="hidden items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-sm sm:flex">
                <Zap className="h-4 w-4 text-primary" />
                <span className="text-muted-foreground">算力：</span>
                <span className="font-semibold text-foreground">{credits}</span>
              </div>

              {/* 用户头像下拉菜单 */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-full px-2 py-1 transition-colors hover:bg-accent"
                  >
                    <span
                      className={cn(
                        'grid h-8 w-8 place-items-center rounded-full border',
                        'bg-gradient-to-tr from-primary/20 to-purple-500/10',
                        'text-sm font-semibold text-primary'
                      )}
                    >
                      {user.username.slice(0, 1)}
                    </span>
                    <span className="hidden text-sm font-medium sm:inline-block">
                      {user.username}
                    </span>
                    <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/me" className="flex cursor-pointer items-center gap-2">
                      <User className="h-4 w-4" />
                      个人中心
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/materials" className="flex cursor-pointer items-center gap-2">
                      <Image className="h-4 w-4" />
                      采集素材
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/updates" className="flex cursor-pointer items-center gap-2">
                      <Megaphone className="h-4 w-4" />
                      系统更新
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/pricing" className="flex cursor-pointer items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      购买套餐
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex cursor-pointer items-center gap-2 text-destructive focus:text-destructive"
                  >
                    <LogOut className="h-4 w-4" />
                    退出登录
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
